import { useCallback, useMemo, useRef } from 'react';
import { useStreaming } from 'src/context/StreamingContext';
import authTokenActions from 'src/store/actions/chatToken';
import messagesActions from 'src/store/actions/message';
import { handleAddInternetSources, handleAddSources } from 'src/store/actions/sources';
import { Errors, Source } from 'src/types';
import { createCites } from 'src/utils/createCites';
import { sortByPosition } from 'src/utils/sortByPosition';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

export type StreamResponse = {
	status: string;
	status_code: number;
	detail: string;
	data: Array<Source | string>;
};
type HeadersInit = Headers | [string, string][] | Record<string, string>;

export type StreamMessageOptions = {
	onChunk: (chunk: string) => void;
	onComplete: (fullText: string) => void;
};

export const useStreamMessage = (
	userInput: string,
	file: File | null | undefined,
	widget: string | undefined,
	{ onChunk, onComplete }: StreamMessageOptions
): (() => Promise<void>) => {
	const dispatch = useAppDispatch();
	const { updateStreamInfo, clearStreamInfo } = useStreaming();
	const chatTokenStore = useAppSelector((s: { chatToken: string }) => s.chatToken);
	const storeAiModel = useAppSelector((s: { aiModel: string }) => s.aiModel);

	const baseApi = useMemo(() => (__SERVER__ ? process?.env?.API_URL : window?._env_?.API_URL), []);
	const aiModel = useMemo(() => (__DEV__ ? storeAiModel : window?._env_?.AI_MODEL), [storeAiModel]);
	const decoder = useMemo(() => new TextDecoder(), []);

	const finalSourcesRef = useRef<Source[]>([]);
	const finalInternetSourcesRef = useRef<Source[]>([]);
	const fullTextRef = useRef<string>('');
	const isFinishedRef = useRef<boolean>(false);

	const handleUsedSource = useCallback(
		(
			dataAsSource: Source,
			isInternet: boolean,
			questionId: string,
			sourcesMap: Map<string, Source>,
			internetSourcesMap: Map<string, Source>
		): void => {
			if (dataAsSource.outdated) {
				dispatch(messagesActions.addMessageOutdated(true));
			}

			const currentMap = isInternet ? internetSourcesMap : sourcesMap;
			const sourceKey = `${dataAsSource.id}-${isInternet ? 'internet' : 'normal'}`;
			currentMap.set(sourceKey, dataAsSource);

			const sources = sortByPosition(Array.from(currentMap.values()));
			const citeMarkdown = createCites(dataAsSource);

			onChunk(citeMarkdown);
			fullTextRef.current += citeMarkdown;

			if (isInternet) {
				finalInternetSourcesRef.current = sources;
				dispatch(handleAddInternetSources(questionId, sources));
			} else {
				finalSourcesRef.current = sources;
				dispatch(handleAddSources(questionId, sources));
			}
		},
		[dispatch, onChunk]
	);

	const retrieveMessage = useCallback(async () => {
		let response: Response;
		const baseHeaders: HeadersInit = {
			'X-Chat-Token': chatTokenStore
		};
		const controller = new AbortController();
		const timeoutId = setTimeout(() => {
			controller.abort();
		}, 60000);

		try {
			if (file) {
				const formData = new FormData();
				formData.append('message', userInput);
				formData.append('model', aiModel || 'gpt-4o-mini');
				formData.append('file', file);
				if (widget) formData.append('widget', widget);

				response = await fetch(`${baseApi}/chat/image`, {
					method: 'POST',
					credentials: 'include',
					headers: baseHeaders,
					body: formData,
					signal: controller.signal
				});
			} else {
				response = await fetch(`${baseApi}/chat/`, {
					method: 'POST',
					credentials: 'include',
					headers: {
						...baseHeaders,
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						message: userInput,
						model: aiModel,
						...(widget && { widget })
					}),
					signal: controller.signal
				});
			}

			clearTimeout(timeoutId);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error('No reader available');
			}

			isFinishedRef.current = false;
			finalSourcesRef.current = [];
			finalInternetSourcesRef.current = [];
			fullTextRef.current = '';
			clearStreamInfo();

			const sourcesMap = new Map<string, Source>();
			const internetSourcesMap = new Map<string, Source>();
			const questionId: string = response.headers.get('x-question-id') || `temp-${Date.now()}`;
			const chatToken = response.headers.get('x-chat-token');

			const finalizeMessage = () => {
				if (!isFinishedRef.current) {
					isFinishedRef.current = true;
					if (!chatTokenStore && chatToken) {
						dispatch(authTokenActions.addChatToken(chatToken));
					}
					onComplete(fullTextRef.current);
					dispatch(
						messagesActions.completeMessageStream(questionId, finalSourcesRef.current, finalInternetSourcesRef.current, fullTextRef.current)
					);
				}
			};

			const processChunk = async (): Promise<void> => {
				let buffer = '';
				let messageHasFinished = false;

				const processLine = (line: string): void => {
					try {
						const chunk: StreamResponse = JSON.parse(line);
						const { detail, data, status: chunkStatus } = chunk;

						switch (chunkStatus) {
							case 'ERROR':
								dispatch({ type: 'error/show', payload: Errors[detail] });
								dispatch(messagesActions.addMessageChunk(Errors[detail], true));
								break;
							case 'WARNING':
								dispatch(messagesActions.addMessageChunk(Errors[detail], true));
								break;
							case 'INFO':
								if (detail === '___ALL_SOURCES_ORDERED___') {
									const allSources = data as Source[];
									finalSourcesRef.current = allSources.filter((s) => s.verified_article === true);
									finalInternetSourcesRef.current = allSources.filter((s) => s.verified_article === false);

									for (const s of finalSourcesRef.current) sourcesMap.set(`${s.id}-normal`, s);
									for (const s of finalInternetSourcesRef.current) internetSourcesMap.set(`${s.id}-internet`, s);

									dispatch(handleAddSources(questionId, finalSourcesRef.current));
									dispatch(handleAddInternetSources(questionId, finalInternetSourcesRef.current));
								} else if (detail === '___VERIFICATION_LABEL___') {
									messageHasFinished = true;
								} else if (detail === '___REASONING_LOG___') {
									updateStreamInfo({ reasoningLog: data[0] as string });
								} else if (detail === '___SEARCH_QUERIES___') {
									updateStreamInfo({ searchQueries: data as string[] });
								} else if (detail === '___TRANSFORMED_QUERY___') {
									updateStreamInfo({ transformedQuery: data[0] as string });
								} else if (detail === '___RESPONSE___') {
									const textChunk = data[0] as string;
									onChunk(textChunk);
									fullTextRef.current += textChunk;
								}
								break;
							default:
								if (detail === '___RESPONSE___') {
									const textChunk = data[0] as string;
									onChunk(textChunk);
									fullTextRef.current += textChunk;
								} else if (detail === '___USED_SOURCE___') {
									const source = data as unknown as Source;
									const isInternet = source.verified_article === false;
									handleUsedSource(source, isInternet, questionId, sourcesMap, internetSourcesMap);
								} else if (detail === '___USED_SOURCE_INTERNET___') {
									const source = data as unknown as Source;
									const isInternet = source.verified_article === false;
									handleUsedSource(source, isInternet, questionId, sourcesMap, internetSourcesMap);
								}
						}
					} catch (err) {
						console.error('Error parsing chunk:', err);
					}
				};

				try {
					while (true) {
						const { done, value } = await reader.read();

						if (done) {
							if (buffer.trim()) {
								processLine(buffer);
							}
							finalizeMessage();
							return;
						}

						buffer += decoder.decode(value, { stream: true });
						const parts = buffer.split('\n');
						buffer = parts.pop() ?? '';

						for (const line of parts) {
							if (!line) continue;
							processLine(line);
						}

						if (messageHasFinished) {
							finalizeMessage();
							return;
						}
					}
				} catch (error) {
					console.error('Error in stream processing:', error);
					throw error;
				}
			};

			await processChunk();

			await processChunk();
		} catch (error) {
			clearTimeout(timeoutId);
			console.error('Error in retrieveMessage:', error);
			dispatch({ type: 'error/show', payload: 'error-back' });
			dispatch(messagesActions.completeMessageStream(`error-${Date.now()}`, [], [], 'error-back', true));
		}
	}, [
		userInput,
		file,
		widget,
		baseApi,
		aiModel,
		chatTokenStore,
		decoder,
		dispatch,
		handleUsedSource,
		onChunk,
		onComplete,
		updateStreamInfo,
		clearStreamInfo
	]);

	return retrieveMessage;
};
